/**
 * Records attached to a lesson, and to the copies made of it.
 *
 * An observation or a mark names the lesson it was made for in `lessonId`.
 * Duplicating that lesson onto another date attaches the record to the copy
 * too, through `extraLessonIds`, rather than cloning the record: a note is
 * one thing that happened to one pupil, and a mark counted twice would skew
 * the report. `lessonId` therefore stays the origin — it is what the report
 * groups marks by — and the extras only ever add places the record shows up.
 */

/** The part of a record that ties it to lessons. */
export interface LessonLinked {
  lessonId: string;
  extraLessonIds: readonly string[];
}

/** Every lesson a record belongs to, its origin first. */
export function linkedLessonIds(row: LessonLinked): string[] {
  return [...new Set([row.lessonId, ...row.extraLessonIds].filter((id) => id !== ''))];
}

/** Whether a record belongs to `lessonId`, as its origin or through a copy. */
export function isLinkedTo(row: LessonLinked, lessonId: string): boolean {
  return lessonId !== '' && (row.lessonId === lessonId || row.extraLessonIds.includes(lessonId));
}

/**
 * The record, also attached to `copyId` — or null when there is nothing to
 * change, because it has nothing to do with `sourceId` or already knows the
 * copy. Following the extras as well as the origin is what makes a copy of a
 * copy still carry the notes of the first lesson.
 */
export function withCopiedLink<T extends LessonLinked>(
  row: T,
  sourceId: string,
  copyId: string,
): T | null {
  if (!isLinkedTo(row, sourceId) || isLinkedTo(row, copyId)) return null;
  return { ...row, extraLessonIds: [...row.extraLessonIds, copyId] };
}
